import * as React from 'react';
import PropTypes from 'prop-types';
import MuiDrawer from '@mui/material/Drawer';

import { callAll } from 'catering/helpers';

const DrawerContext = React.createContext(null);
DrawerContext.displayName = 'DrawerContext';

function Drawer({ initialIsOpen = false, ...rest }) {
  const [isOpen, setIsOpen] = React.useState(initialIsOpen);
  return <DrawerContext.Provider value={[isOpen, setIsOpen]} {...rest} />;
}

Drawer.propTypes = {
  initialIsOpen: PropTypes.bool
};

function useDrawerContext() {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error(`useDrawerContext must be used within a DrawerContext`);
  }
  return context;
}

/**
 * Although Drawer can be opened by default when rendered. In some cases, we'll
 * want to have it not render by default and delay the rendering when opening.
 * An example of this would be inside our menu sub-routes that have a small
 * delay to render the route, so the transition of the modal opening isn't seen.
 * This is to fake that delay to improve the user experience.
 * @param {number} timeInMilliseconds - delay time in milliseconds
 * @returns {function(): WindowOrWorkerGlobalScope.setTimeout | *} - callback
 *  to open modal with delay
 */
function useOpenDrawerWithDelay(timeInMilliseconds = 1) {
  const [, setIsOpen] = useDrawerContext();
  return () => setTimeout(() => setIsOpen(true), timeInMilliseconds);
}

function DrawerOpenButton({ children: child }) {
  const [, setIsOpen] = useDrawerContext();
  return React.cloneElement(child, {
    onClick: callAll(() => setIsOpen(true), child.props.onClick)
  });
}

function DrawerDismissButton({ children: child }) {
  const [, setIsOpen] = useDrawerContext();
  return React.cloneElement(child, {
    onClick: callAll(() => setIsOpen(false), child.props.onClick)
  });
}

function DrawerContentsBase({ onClose, ...rest }) {
  const [isOpen, setIsOpen] = useDrawerContext();
  return (
    <MuiDrawer
      anchor="right"
      onClose={callAll(() => setIsOpen(false), onClose)}
      open={isOpen}
      data-testid="drawer"
      {...rest}
    />
  );
}

DrawerContentsBase.propTypes = {
  onClose: PropTypes.func
};

function DrawerContentsWithOpenDelay(props) {
  useOpenDrawerWithDelay()();
  return <DrawerContentsBase {...props} />;
}

export {
  Drawer,
  DrawerContentsBase,
  DrawerContentsWithOpenDelay,
  DrawerDismissButton,
  DrawerOpenButton,
  useDrawerContext,
  useOpenDrawerWithDelay
};
